import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { dbHelpers } from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import TradeForm from './TradeForm'
import TradesList from './TradesList'
import * as FiIcons from 'react-icons/fi'

const { FiPlus, FiFilter, FiSearch, FiAlertCircle, FiCheckCircle } = FiIcons

const Journal = () => {
  const { user } = useAuth()
  const location = useLocation()
  const [trades, setTrades] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [editingTrade, setEditingTrade] = useState(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterBy, setFilterBy] = useState('all')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    if (user) {
      loadTrades()
    }
    if (location.state?.openForm) {
      setShowForm(true)
      window.history.replaceState({}, document.title)
    }
  }, [user, location.state])

  const loadTrades = async () => {
    try {
      setLoading(true)
      const tradesData = await dbHelpers.getUserTrades(user.id, 100)
      setTrades(tradesData)
    } catch (error) {
      console.error('Error loading trades:', error)
      setError('Failed to load trades. Please refresh the page.')
    } finally {
      setLoading(false)
    }
  }

  const handleTradeSubmit = async (tradeData) => {
    try {
      setError('')
      setSuccess('')
      if (editingTrade) {
        await dbHelpers.updateTrade(editingTrade.id, tradeData)
        setSuccess('Trade updated successfully!')
      } else {
        await dbHelpers.addTrade({ ...tradeData, user_id: user.id })
        setSuccess('New trade added to your journal!')
      }
      await loadTrades()
      setShowForm(false)
      setEditingTrade(null)
      setTimeout(() => setSuccess(''), 3000)
    } catch (error) {
      console.error('Error saving trade:', error)
      setError(error.message || 'Failed to save trade. Please check your inputs.')
    }
  }

  const handleDeleteTrade = async (tradeId) => {
    try {
      setError('')
      await dbHelpers.deleteTrade(tradeId)
      setTrades(prev => prev.filter(t => t.id !== tradeId))
      setSuccess('Trade deleted successfully')
      setTimeout(() => setSuccess(''), 3000)
    } catch (error) {
      console.error('Delete error:', error)
      setError('Failed to delete trade. Please try again.')
    }
  }

  const handleEditTrade = (trade) => {
    setEditingTrade(trade)
    setShowForm(true)
  }

  const filteredTrades = trades.filter(trade => {
    const symbolMatch = trade.symbol?.toLowerCase().includes(searchTerm.toLowerCase())
    const setupMatch = trade.setup_type?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesSearch = symbolMatch || setupMatch
    const matchesFilter = filterBy === 'all' || 
      (filterBy === 'wins' && (trade.pnl || 0) > 0) || 
      (filterBy === 'losses' && (trade.pnl || 0) < 0) || 
      (filterBy === 'buys' && trade.side === 'buy') || 
      (filterBy === 'sells' && trade.side === 'sell')
    return matchesSearch && matchesFilter
  })

  return (
    <div className="space-y-8 relative pb-24 md:pb-0">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
      >
        <header>
          <h1 className="text-4xl font-black tracking-tight text-gray-900 dark:text-white bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400">
            Trading Journal
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2 font-medium">
            Record and track your trading activities in real-time
          </p>
        </header>
        <button 
          onClick={() => { setError(''); setShowForm(true); }}
          className="hidden md:flex items-center space-x-2 lux-button-primary px-8"
        >
          <SafeIcon icon={FiPlus} className="h-5 w-5" />
          <span className="font-bold">Add Trade</span>
        </button>
      </motion.div>

      {/* Notifications */}
      <div className="space-y-4">
        {error && (
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center space-x-3 text-red-500 shadow-sm"
          >
            <SafeIcon icon={FiAlertCircle} className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm font-bold">{error}</p>
          </motion.div>
        )}
        {success && (
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="p-4 bg-green-500/10 border border-green-500/20 rounded-2xl flex items-center space-x-3 text-green-500 shadow-sm"
          >
            <SafeIcon icon={FiCheckCircle} className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm font-bold">{success}</p>
          </motion.div>
        )}
      </div>

      {/* Filters & Search */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="lux-card p-6"
      >
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <SafeIcon icon={FiSearch} className="h-5 w-5 text-gray-400" />
            </div>
            <input 
              type="text" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search symbol or setup..."
              className="lux-input pl-12"
            />
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-white/10 px-4 py-3 rounded-2xl">
              <SafeIcon icon={FiFilter} className="h-4 w-4 text-lux-primary" />
              <select 
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
                className="bg-transparent border-none text-sm font-bold text-gray-900 dark:text-white focus:ring-0 outline-none cursor-pointer"
              >
                <option value="all" className="bg-white dark:bg-slate-900 text-gray-900 dark:text-white">All Trades</option>
                <option value="wins" className="bg-white dark:bg-slate-900 text-gray-900 dark:text-white">Wins Only</option>
                <option value="losses" className="bg-white dark:bg-slate-900 text-gray-900 dark:text-white">Losses Only</option>
                <option value="buys" className="bg-white dark:bg-slate-900 text-gray-900 dark:text-white">Longs</option>
                <option value="sells" className="bg-white dark:bg-slate-900 text-gray-900 dark:text-white">Shorts</option>
              </select>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Trade Form Modal */}
      {showForm && (
        <TradeForm 
          trade={editingTrade} 
          onSubmit={handleTradeSubmit} 
          onClose={() => { setShowForm(false); setEditingTrade(null); }} 
        />
      )}

      {/* Trades List */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <TradesList 
          trades={filteredTrades} 
          loading={loading} 
          onEditTrade={handleEditTrade}
          onDeleteTrade={handleDeleteTrade}
        />
      </motion.div>

      {/* Mobile Floating Action Button */}
      <div className="md:hidden fixed bottom-24 right-6 z-40">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => { setError(''); setShowForm(true); }}
          className="flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-br from-lux-primary to-lux-accent text-white shadow-2xl shadow-lux-primary/40 border border-white/20"
        >
          <SafeIcon icon={FiPlus} className="h-8 w-8" />
        </motion.button>
      </div>
    </div>
  )
}

export default Journal